<script>
    import listeners from '../private/listeners';
    import telemetry from '../private/telemetry';
    import $locale from '../../prototype/locale';

    const defaultLayout = $build.platform == 'web' || $build.platform == 'marketing' ? 'scroll' : 'full';
    const emDash = '—';


    /**
     * This is an inline view that encapsulates an application. There will generally be one
     * instance in a front end project repository, but it is not a singleton and can be nested
     * within another application. This component manages the loading of translations and the
     * docking of overlays.
     *
     * Slots:
     * - `default`: Main application content.
     * - `error`: Content displayed as an alternative to the application if a promise returned from
     *   `prepare` is rejected. If this is not provided, the default slot will be displayed. If this
     *   is specified as a scoped slot, the object that was thrown will be provided as the `error`
     *   prop.
     * - `header`: Content that is always displayed at the beginning of the page and is not subject
     *   to web viewport width limitation. This content is still obscured if a modal is displayed.
     * - `footer`: Content that is always displayed at the end of the page and is not subject to
     *   web viewport width limitation. This content is still obscured if a modal is displayed.
     */
    export default {
        name: 'ib-application',

        mixins: [
            listeners,
            telemetry
        ],

        props: {
            /**
             * Layout mode; `full`, `scroll`, and `widget` are supported. `full` means fill the
             * entire window and handle any scrolling inside the application container. This is
             * the default. `scroll` means fill at least the height of the window and display a
             * vertical scrollbar; scrolling will be handled by the document body. `widget` means
             * fit the application to the height of the content and do not apply a background;
             * this is useful when displaying a dynamically sized WebView inlined in a native app.
             * This setting is not meaningful for applications embedded inside other applications.
             */
            layout: {
                type: String,
                default: null
            },
            /**
             * Translations loaded either via fetched JSON at runtime or via require() at build.
             * Keys are IETF locale strings; more than one of them may match the current locale.
             */
            translations: Object,
            /**
             * A function to initialize the application; this will be called on load and if it
             * returns a Promise, content will not be displayed until the Promise resolves.
             */
            prepare: Function,
            /**
             * Lib.Common behavior flags; this is used to roll out breaking behavior.
             */
            flags: Object,
            /**
             * If true, treat this element as an `ib-row`.
             */
            row: Boolean,
            /**
             * If true and this is a row element, expand it to fill available vertical space.
             */
            grow: Boolean
        },

        data() {
            return {
                ready: false,
                error: false,
                direction: 'ltr',
                key: null,
                txStore: [],
                tmListen: false,
                hosted_: []
            };
        },

        watch: {
            translations: 'loadTranslations',
            '$locale.current': 'loadTranslations',
            layout: 'setLayout'
        },

        created() {
            // If not root application, clone $root to create new translation scope
            if (this.$root.__app) {
                const root = { ...this.$root };
                const slot = this.$slots.default;
                if (slot) {
                    slot.forEach(ref => {
                        if (ref.context) {
                            ref.context.$root = root;
                        }
                    });
                }
                this.$root = root;
            }
            this.$root.__app = this;
            this.$root.__txl = {};

            this.txStore = { ...this.translations };
            this.loadTranslations();

            const onPrepare = this.prepare ? this.prepare() : null;
            if (onPrepare instanceof Promise) {
                onPrepare
                    .then(() => {
                        this.ready = true;
                    })
                    .catch(error => {
                        this.error = error || true;
                        this.ready = true;
                    });
            } else {
                this.ready = true;
            }
        },

        mounted() {
            this.setLayout(this.layout);
        },

        beforeDestroy() {
            this.setLayout(void 0, this.layout);
        },

        render(h) {
            // this._e() renders a comment; without it, keep-alive does not work
            let child;
            if (this.key) {
                const header = this.$slots.header;
                const footer = this.$slots.footer;
                const error = this.error;
                child = [
                    header
                        ? h('div', {
                                'staticClass': '_apph'
                            }, header)
                        : this._e(),
                    this.ready
                        ? h('div', {
                                'staticClass': '_appi',
                                'class': {
                                    _row: this.row,
                                    grow: this.grow
                                }
                            }, error
                                ? this.$slots.error || (this.$scopedSlots.error ? this.$scopedSlots.error({ error }) : void 0)
                                : this.$slots.default || (this.$scopedSlots.default ? this.$scopedSlots.default() : void 0)
                            )
                        : this._e(),
                    footer
                        ? h('div', {
                                'staticClass': '_appf'
                            }, footer)
                        : this._e()
                ];
                if (this.hosted_.length) {
                    child = child.concat(this.hosted_.map(dialog => dialog.renderSelf(h, this)));
                }
            } else {
                child = [];
            }

            return h('div', {
                'staticClass': '_app _con',
                'on': this.listeners,
                'key': this.key,
                'attrs': {
                    dir: this.direction
                }
            }, child);
        },

        methods: {
            setLayout(to, from) {
                if (this.$el.parentElement === document.body) {
                    if (from !== void 0) {
                        const layout = from || defaultLayout;
                        document.documentElement.classList.remove('_l' + layout);
                        if (layout == 'full') {
                            window.scrollTo = this.scrollTo;
                            window.scrollBy = this.scrollBy;
                        }
                    }
                    if (to !== void 0) {
                        const layout = to || defaultLayout;
                        document.documentElement.classList.add('_l' + layout);
                        if (layout == 'full') {
                            // Overload `window.scrollTo` and `window.scrollBy`
                            this.scrollTo = window.scrollTo;
                            this.scrollBy = window.scrollBy;
                            window.scrollTo = (...args) => this.$el.scrollTo(...args);
                            window.scrollBy = (...args) => this.$el.scrollBy(...args);
                        }
                    }
                }
            },

            loadTranslations() {
                const txStore = this.txStore;
                const language = $locale.language;
                const region = $locale.region;
                const script = $locale.script;

                let all = [
                    language,
                    language + '-' + script,
                    language + '-' + region,
                    language + '-' + script + '-' + region
                ].filter(lc => txStore.hasOwnProperty(lc));

                // If no translations available for current locale, trigger 'en'
                if (!all.length && txStore['en']) {
                    all = ['en'];
                }

                // Load translations not previously loaded
                Promise.all(all
                    .filter(language => typeof txStore[language] === 'string')
                    .map(language => fetch(txStore[language])
                        .then(response => response.json())
                        .then(response => txStore[language] = response)
                        .catch(error => {
                            console.error('Failed to load ' + txStore[language]);
                        })))
                    .then(this.mapTranslations);
            },

            mapTranslations() {
                const language = $locale.language;
                const txStore = this.txStore;

                this.direction = (language == 'ar' || language == 'he') && Object.keys(this.translations||{}).some(key => key.startsWith(language))
                    ? 'rtl'
                    : 'ltr';

                this.$root.__txl = {
                    ...txStore['en'],
                    ...txStore[language],
                    ...txStore[language + '-' + $locale.script],
                    ...txStore[language + '-' + $locale.region],
                    ...txStore[language + '-' + $locale.script + '-' + $locale.region]
                };

                this.$t = function(key, replacements) {
                    const result = this.$root.__txl[key] || key;
                    return replacements === void 0
                        ? result
                        : result.replace(/{(\w+)}/g, (all, key) => {
                            const val = replacements[key];
                            return val == null ? emDash : val;
                        });
                };

                if (this.key) {
                    // Force rerender of all components including keep-alive
                    this.key = null;
                    this.$nextTick(() => {
                        this.key = $locale.current;
                    });
                } else {
                    // Trigger initial render
                    this.key = $locale.current;
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._app {
        display: flex;
        flex-direction: column;
        position: relative;
        width: 100%;
        height: 100%;
    }
    ._appi {
        flex: 1 0 auto;
    }
    ._apph,
    ._appf {
        flex: 0 0 auto;
    }

    /** Layout */
    @media screen {
        ._lfull,
        ._lscroll {
            width: 100%;
            height: 100%;
        }
        ._lfull > body {
            width: 100%;
            height: 100%;
            overflow: hidden;
            background: @bg;
        }
        ._lfull > body > ._app {
            overflow: auto;
            -webkit-overflow-scrolling: touch;
        }
        ._lscroll > body {
            width: 100%;
            height: 100%;
            overflow-x: auto;
            overflow-y: scroll;
            background: @bg;
        }
        ._lscroll > body > ._app {
            height: auto;
            min-height: 100%;
        }
    }
</style>
